/**
 * Booking form anchor for Smart Dynamic Flow.
 * Doctor wins once selected; date is only the anchor when chosen first (no doctor yet).
 */
export type BookingAnchor = 'none' | 'date' | 'doctor';

export const resolveBookingAnchor = (input: {
  doctorId?: string | null;
  hasDate?: boolean;
}): BookingAnchor => {
  if (input.doctorId) return 'doctor';
  if (input.hasDate) return 'date';
  return 'none';
};

export type DoctorsListOptionsInput = {
  clinicId: string;
  doctorId?: string;
  dateKey?: string;
  excludeAppointmentId?: string;
};

/**
 * Params for the stable doctors dropdown query.
 * - doctor / none: clinic list only (no date) so changing the calendar does not reshuffle doctors
 * - date: include date so doctors can be annotated working / not_working
 * Duration / type / chair are omitted — they only affect slot conflict status, not the doctor roster.
 */
export const buildDoctorsListOptionsParams = (
  input: DoctorsListOptionsInput
): {
  clinicId: string;
  date?: string;
  excludeAppointmentId?: string;
} => {
  const anchor = resolveBookingAnchor({
    doctorId: input.doctorId,
    hasDate: Boolean(input.dateKey),
  });

  const base = {
    clinicId: input.clinicId,
    excludeAppointmentId: input.excludeAppointmentId,
  };

  if (anchor === 'date' && input.dateKey) {
    return {
      ...base,
      date: input.dateKey,
    };
  }

  return base;
};

/** Only date-anchored flows should drop a doctor marked not_working. */
export const shouldClearDoctorForNotWorking = (anchor: BookingAnchor): boolean =>
  anchor === 'date';
